// src/components/alerts/mobile_AlertDropdown.jsx
import React, { useEffect, useState, useRef } from "react";
import api from "../../api/axios";
import AlertItem from "./AlertItem";
import "./AlertDropdown.css";

const PAGE_SIZE = 10;

const MobileAlertDropdown = ({
  isOpen,
  setOpen,
  setUnreadCount,
  triggerRef,
}) => {
  const [alerts, setAlerts] = useState([]);
  const [visibleAlerts, setVisibleAlerts] = useState([]);
  const [page, setPage] = useState(1);

  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);

  // 🔔 toast 중복 방지용
  const prevAlertIdsRef = useRef(new Set());
  const pollingRef = useRef(null);

  // 알림창 열릴 때 최초 로드 및 권한 요청
  useEffect(() => {
    if (isOpen) {
      loadLatestAlerts();

      // 모바일에서는 사용자 액션(버튼 클릭으로 인한 open) 시 권한 요청이 더 잘 작동함
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          console.log('mobile 알림 권한 결과:', permission);
        });
      }
    }
  }, [isOpen]);

  /**
   * 🔄 polling
   */
  const pollNewAlerts = async () => {
    try {
      const res = await api.get("alerts/");
      const list = res.data.alerts || [];

      // 최초 기준 설정
      if (prevAlertIdsRef.current.size === 0) {
        prevAlertIdsRef.current = new Set(list.map((a) => a.id));
        setAlerts(list);
        setUnreadCount(list.filter((a) => !a.is_read).length);
        return;
      }

      const prevIds = prevAlertIdsRef.current;

      const newAlerts = list.filter(
        (a) => !prevIds.has(a.id) && !a.is_read && !isOpen
      );

      newAlerts.forEach((alert) => {
        // 브라우저 알림 (탭이 백그라운드일 때)
        if ('Notification' in window && Notification.permission === 'granted') {
          const doc = alert.document;
          const notificationTitle = alert.is_urgent ? '[긴급 알림]' : '[새 알림]';
          const notificationBody = alert.message;

          // 문서 URL 생성
          const targetUrl = doc
            ? `/project/${doc.project}/${doc.module}/${doc.doc_type}/${doc.dns}/view`
            : alert.project
              ? `/project/${alert.project}/docs/data`
              : '/';

          // Service Worker를 통한 알림 표시
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'SHOW_NOTIFICATION',
              title: notificationTitle,
              options: {
                body: notificationBody,
                icon: '/mobile_chatbot.png',
                badge: '/logo192.ico',
                vibrate: alert.is_urgent ? [200, 100, 200, 100, 200] : [200],
                tag: `alert-${alert.id}`,
                requireInteraction: alert.is_urgent,
                data: { url: targetUrl }
              }
            });
          } else {
            // Fallback: Service Worker 없으면 일반 알림
            const notification = new Notification(notificationTitle, {
              body: notificationBody,
              icon: '/mobile_chatbot.png',
              vibrate: alert.is_urgent ? [200, 100, 200, 100, 200] : [200],
              tag: `alert-${alert.id}`,
            });

            notification.onclick = () => {
              window.focus();
              window.location.href = targetUrl;
              notification.close();
            };
          }
        }
      });

      // 기준 갱신
      prevAlertIdsRef.current = new Set(list.map((a) => a.id));

      setAlerts(list);
      setUnreadCount(list.filter((a) => !a.is_read).length);
    } catch (e) {
      console.error("mobile alert polling error", e);
    }
  };

  /**
   * 🔁 polling 시작
   */
  useEffect(() => {
    pollNewAlerts();
    pollingRef.current = setInterval(pollNewAlerts, 10000);

    return () => clearInterval(pollingRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 알림 불러오기 함수
  const loadLatestAlerts = async () => {
    const res = await api.get(`alerts/`);
    const list = res.data.alerts || [];
    setAlerts(list);

    // 첫 10개만 출력
    setVisibleAlerts(list.slice(0, PAGE_SIZE));
    setPage(1);

    const unread = list.filter((a) => !a.is_read).length;
    setUnreadCount(unread);
  };

  // 알림창 열릴 때 최초 로드
  useEffect(() => {
    if (isOpen) {
      loadLatestAlerts();
    }
  }, [isOpen]);

  // 읽음 처리
  const handleRead = async (id) => {
    await api.post(`alerts/${id}/read/`);
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, is_read: true } : a))
    );

    setUnreadCount((prev) => Math.max(prev - 1, 0));
    setOpen(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`alerts/${id}/delete/`);
      setAlerts((prev) => prev.filter((a) => a.id !== id)); // 리스트에서 제거

      // 읽지 않은 알림을 삭제한 경우 count 감소
      setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
    } catch (e) {
      console.error(e);
    }
  };

  // 무한 스크롤 로직
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isLoadingTop = false; // 중복 로딩 방지

    const handleScroll = async () => {
      // 1) 맨 아래 → 다음 10개 로드
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
        const nextPage = page + 1;
        const nextItems = alerts.slice(0, nextPage * PAGE_SIZE);

        if (nextItems.length > visibleAlerts.length) {
          setVisibleAlerts(nextItems);
          setPage(nextPage);
        }
      }

      // 2) 맨 위 → 최신 알림 새로고침
      if (el.scrollTop === 0 && !isLoadingTop) {
        isLoadingTop = true;
        await loadLatestAlerts();

        // 로딩 후 상단 유지
        setTimeout(() => {
          isLoadingTop = false;
        }, 300);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [alerts, page, visibleAlerts]);

  // 바깥 클릭 시 자동 닫힘 (벨 버튼은 예외)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const dropdownEl = dropdownRef.current;
      const triggerEl = triggerRef?.current;

      const clickInsideDropdown = dropdownEl && dropdownEl.contains(e.target);
      const clickOnTrigger = triggerEl && triggerEl.contains(e.target);

      if (!clickInsideDropdown && !clickOnTrigger) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setOpen, triggerRef]);

  return (
    <div className={!isOpen ? "hidden" : ""}>
      {/* 반투명 배경 */}
      <div
        className="fixed inset-0 bg-black/35 backdrop-blur-[1px] z-[45]"
        onClick={() => setOpen(false)}
      />

      {/* 패널 */}
      <div className="fixed inset-0 z-50 flex flex-col items-center px-4 pt-[72px] pointer-events-none">
        <div
          className="relative w-full max-w-[520px] shadow-2xl rounded-2xl bg-white border border-gray-200 overflow-hidden pointer-events-auto"
          ref={dropdownRef}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
            <h3 className="text-sm font-bold text-gray-900">알림 센터</h3>
            <div className="text-xs text-gray-600 font-medium">
              전체 {alerts.length} · 미확인 {alerts.filter((a) => !a.is_read).length}
            </div>
          </div>

          {/* 리스트 */}
          <div
            ref={scrollRef}
            className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100 custom-scrollbar"
          >
            {visibleAlerts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">알림이 없습니다</p>
                <p className="text-xs text-gray-500">새로운 알림이 도착하면 여기에 표시됩니다</p>
              </div>
            ) : (
              <>
                <ul className="flex flex-col">
                  {visibleAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      onRead={handleRead}
                      onDelete={handleDelete}
                    />
                  ))}
                </ul>

                {visibleAlerts.length >= alerts.length && (
                  <div className="py-4 text-center text-[11px] text-gray-400 select-none bg-gray-50">
                    <p className="font-medium">추가로 표시할 알림이 없습니다</p>
                    <p className="mt-1">알림은 최대 7일간 보관됩니다</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAlertDropdown;
